// src/components/private/UserAccount.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Col, ListGroup, Button, Container, Row, InputGroup, Form, Badge, Alert, Tooltip, OverlayTrigger, Card } from 'react-bootstrap';
import ConfirmationModal from '../common_components/ConfirmationModal';
import { toast } from 'react-toastify';
import HouseholdDetails from './HouseholdDetails';
import 'react-toastify/dist/ReactToastify.css';
import FloatingActionButton from '../common_components/FloatingActionButton';
import moment from 'moment';

const ViewUserHouseholds = () => {
    const [households, setHouseholds] = useState([]);
    const token = localStorage.getItem('token');
    const currentUserEmail = JSON.parse(localStorage.getItem('userInfo')).email;
    const [inviteEmail, setInviteEmail] = useState('');
    const [showInviteInput, setShowInviteInput] = useState({});
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [memberToRemove, setMemberToRemove] = useState({ householdSlug: '', identifier: '' });
    const [selectedHousehold, setSelectedHousehold] = useState();
    const [showHouseholdModal, setShowHouseholdModal] = useState(false);

    const notifySuccess = (message) => toast.success(message);
    const notifyError = (message) => toast.error(message);

    const fetchUserHouseholdInfo = useCallback(async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/users/households/`, {
                headers: {
                    Authorization: `Token ${token}`,
                },
            });
            const fetchedHouseholds = response.data;
            setHouseholds(fetchedHouseholds);

            // Automatically select the first household if available
            if (fetchedHouseholds.length > 0) {
                setSelectedHousehold(fetchedHouseholds[0]);
            }
        } catch (error) {
            console.error('Error fetching user households info:', error);
        }
    }, [token]);

    useEffect(() => {
        fetchUserHouseholdInfo();
        document.title = 'Saarthi - Households';
    }, [fetchUserHouseholdInfo]);

    const toggleInviteInput = (householdSlug) => {
        setShowInviteInput(prevState => ({
            ...prevState,
            [householdSlug]: !prevState[householdSlug]
        }));
    };

    const handleShowRemoveConfirmation = (householdSlug, identifier) => {
        setMemberToRemove({ householdSlug, identifier });
        setShowConfirmationModal(true);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'ACCEPTED':
                return 'success';
            case 'PENDING':
                return 'warning';
            case 'DECLINED':
                return 'danger';
            case 'EXPIRED':
                return 'secondary';
            case 'OWNER':
                return 'primary';
            default:
                return 'secondary';
        }
    };

    const handleSendInvitation = async (householdSlug, email) => {
        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/users/households/${householdSlug}/invite/`, { email }, {
                headers: {
                    Authorization: `Token ${token}`,
                },
            });
            notifySuccess('Invitation sent successfully.');
            fetchUserHouseholdInfo();
            setInviteEmail('');
        } catch (error) {
            console.error('Error sending invitation:', error);
            toast.error(error.response.data.detail);
        }
    };

    const handleInvitationResponse = async (invitationToken, response) => {
        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/users/households/invitations/${invitationToken}/`, { response }, {
                headers: {
                    Authorization: `Token ${token}`,
                },
            });
            fetchUserHouseholdInfo();
        } catch (error) {
            console.error('Error updating invitation status:', error);
        }
    };

    const handleRemoveUser = async (householdSlug, identifier) => {
        try {
            await axios.delete(`${process.env.REACT_APP_BACKEND_API_URL}/users/households/${householdSlug}/remove_member/${identifier}/`, {
                headers: {
                    Authorization: `Token ${token}`,
                },
            });
            notifySuccess('User removed successfully.');
            fetchUserHouseholdInfo();
        } catch (error) {
            console.error('Error removing user:', error);
            notifyError('Failed to remove user.');
        }
    };

    const handleShowHouseholdModal = (household) => {
        setSelectedHousehold(household);
        setShowHouseholdModal(true);
    };

    return (
        <Container>
            <Row>
                <Col md={4}>
                    <h5>My Households</h5>
                    {households.length > 0 ? (
                        <ListGroup variant="flush" style={{ maxHeight: '300px', overflowY: 'auto' }} className='mt-3'>
                            {households.map((household) => (
                                <ListGroup.Item
                                    key={household.slug}
                                    onClick={() => setSelectedHousehold(household)}
                                    active={selectedHousehold && selectedHousehold.slug === household.slug}
                                    className="d-flex justify-content-between align-items-center"
                                    style={{ cursor: 'pointer', paddingLeft: '7px' }}
                                >
                                    <div className="d-flex align-items-center">
                                        {household.name}
                                        {household.is_primary_member && (
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={<Tooltip id={`tooltip-${household.slug}`}>Primary member of this household!</Tooltip>}
                                            >
                                                <Badge bg="info" text="light" className="ms-2">Primary Member</Badge>
                                            </OverlayTrigger>
                                        )}
                                    </div>
                                    <div>
                                        <i className="bi bi-info-circle" onClick={(e) => { handleShowHouseholdModal(household); e.stopPropagation() }}></i>
                                    </div>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    ) : (
                        <Alert variant="warning">No households found.</Alert>
                    )}
                </Col>

                <Col md={8}>
                    <h5>Household Members</h5>
                    {selectedHousehold ? (
                        <Card className="mt-3">
                            <Card.Body>
                                <ListGroup variant="flush">
                                    {selectedHousehold.members.map((member) => (
                                        <ListGroup.Item
                                            key={member.invitation_token}
                                            className="d-flex justify-content-between align-items-center"
                                            style={{ paddingLeft: '7px' }}
                                        >
                                            <div className="d-flex align-items-center">
                                                {member.invitation_status !== 'OWNER' && (
                                                    <OverlayTrigger
                                                        overlay={<Tooltip id={`tooltip-remove-${member.invitation_token}`}>Remove User</Tooltip>}
                                                        placement="top"
                                                    >
                                                        <i className="bi bi-x-circle-fill text-danger me-2"
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={(e) => {
                                                                handleShowRemoveConfirmation(selectedHousehold.slug, member.email);
                                                                e.stopPropagation();
                                                            }}></i>
                                                    </OverlayTrigger>
                                                )}
                                                {member.username}
                                            </div>
                                            <div>
                                                {member.invitation_status === 'PENDING' && member.email === currentUserEmail ? (
                                                    <>
                                                        <Badge bg="warning" text="dark">Pending</Badge>
                                                        <Button variant="success" size="sm" onClick={() => handleInvitationResponse(member.invitation_token, 'accept')} className="ms-2">Accept</Button>
                                                        <Button variant="danger" size="sm" onClick={() => handleInvitationResponse(member.invitation_token, 'reject')} className="ms-2">Reject</Button>
                                                    </>
                                                ) : (
                                                    <OverlayTrigger
                                                        overlay={<Tooltip id={`tooltip-edit-${member.invitation_token}`}>{` Invited on: ${moment(member.invited_on).format('DD-MMM-YYYY')}`}</Tooltip>}
                                                        placement="top"
                                                    >
                                                        {member.invitation_status && (
                                                            <Badge bg={getStatusColor(member.invitation_status)} text="light">
                                                                {member.invitation_status}
                                                            </Badge>
                                                        )}
                                                    </OverlayTrigger>
                                                )}
                                            </div>
                                        </ListGroup.Item>
                                    ))}
                                    {selectedHousehold.members.some(member => member.email === currentUserEmail && member.is_primary_member) && (
                                        <ListGroup.Item
                                            style={{ cursor: 'pointer', paddingLeft: '7px' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleInviteInput(selectedHousehold.slug);
                                            }}
                                        >
                                            <i className="bi bi-person-plus-fill" style={{ marginRight: '5px' }}></i>
                                            Invite Members to Household
                                            <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-invite-${selectedHousehold.slug}`}>Please ensure that user has an account with Saarthi to receive an invitation.</Tooltip>}>
                                                <i className="bi bi-info-circle ms-2"></i>
                                            </OverlayTrigger>
                                            {showInviteInput[selectedHousehold.slug] && (
                                                <InputGroup className="mb-3 mt-2">
                                                    <Form.Control
                                                        placeholder="Recipient's email"
                                                        aria-label="Recipient's email"
                                                        aria-describedby="basic-addon2"
                                                        value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                    <Button onClick={() => handleSendInvitation(selectedHousehold.slug, inviteEmail)} variant="outline-secondary">Send Invite</Button>
                                                </InputGroup>
                                            )}
                                        </ListGroup.Item>
                                    )}
                                </ListGroup>
                            </Card.Body>
                        </Card>
                    ) : (
                        <Alert variant="info">Select a household to view its members.</Alert>
                    )}
                </Col>
            </Row>

            <HouseholdDetails
                show={showHouseholdModal}
                handleClose={() => setShowHouseholdModal(false)}
                selectedHousehold={selectedHousehold}
                refreshHouseholds={fetchUserHouseholdInfo}
            />

            <ConfirmationModal
                show={showConfirmationModal}
                onHide={() => setShowConfirmationModal(false)}
                onConfirm={() => {
                    handleRemoveUser(memberToRemove.householdSlug, memberToRemove.identifier);
                    setShowConfirmationModal(false);
                }}
                title="Confirm Removal"
                body="Are you sure you want to remove this user from the household?"
            />

            <FloatingActionButton
                actions={[
                    {
                        icon: 'bi bi-plus-square',
                        handler: () => { handleShowHouseholdModal(null) },
                        tooltip: 'Add Household',
                        color: '#4b88a2'
                    }
                ]}
            />
        </Container>
    );
};

export default ViewUserHouseholds;